import {
  Badge,
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogTitle,
  Icon,
  Text,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@flatfile/baseline'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

const customSvg = new URL('/images/custom.jpg', import.meta.url).href
const demoSvg = new URL('/images/demo.jpg', import.meta.url).href

export default function CustomAppPath(
  props: { mini: boolean; title?: string; iconPlus?: boolean; onAnyAction?: () => void; hidden?: boolean } = {
    mini: false,
  }
) {
  const [customAppModalOpen, setCustomAppModalOpen] = useState(false)
  const [choice, setChoice] = useState('from scratch')
  const nav = useNavigate()

  const openModal = () => setCustomAppModalOpen(true)
  const closeModal = () => setCustomAppModalOpen(false)

  return (
    <>
      <Dialog open={customAppModalOpen} onOpenChange={setCustomAppModalOpen}>
        <DialogContent
          className='p-0 w-screen h-screen inset-0 m-0 translate-x-0 translate-y-0 top-0 left-0 border-none z-[200] flex flex-col gap-0 max-w-none'
          fullscreen
        >
          <div className='flex justify-between items-center px-6 py-4 border-b'>
            <DialogTitle className='text-xl font-medium'>Build a Custom App</DialogTitle>
            <DialogClose>
              <Icon name='cross-large' size={16} />
            </DialogClose>
          </div>

          <div className='p-6 bg-base-white flex-1 overflow-auto'>
            <div className='text-center mb-6 max-w-4xl mx-auto'>
              <Text variant='title3' emphasized>
                Choose Your Starting Point for Building Your Custom App
              </Text>
            </div>

            <div className='flex flex-row gap-8 pb-4 justify-center max-w-[1300px] mx-auto flex-wrap'>
              <div
                className={`border rounded-lg bg-base-white overflow-hidden flex flex-col w-[390px] h-full cursor-pointer ${
                  choice === 'from scratch' ? 'ring-2 ring-primary border-primary' : ''
                }`}
                onClick={() => setChoice('from scratch')}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    setChoice('from scratch')
                    e.preventDefault()
                  }
                }}
                role='button'
                tabIndex={0}
              >
                <div className='w-full h-[280px] flex justify-center bg-[#F9F9FA] overflow-hidden'>
                  <img src={customSvg} alt='Start from scratch' className='w-full h-[280px] object-cover' />
                </div>
                <div className='p-4'>
                  <div className='flex justify-between items-start mb-2'>
                    <Text variant='title3' emphasized>
                      Start from scratch
                    </Text>
                    <Badge variant='secondary' radius='full' className='flex-shrink-0'>
                      FOR DEVELOPERS
                    </Badge>
                  </div>
                  <Text variant='callout' className='text-text-default'>
                    We'll lead you through the process of building a custom app, step by step, introducing you to
                    Flatfile fundamentals along the way.
                  </Text>
                </div>
              </div>

              <div
                className={`border rounded-lg bg-base-white overflow-hidden flex flex-col w-[390px] h-full cursor-pointer ${
                  choice === 'with examples' ? 'ring-2 ring-primary border-primary' : ''
                }`}
                onClick={() => setChoice('with examples')}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    setChoice('with examples')
                    e.preventDefault()
                  }
                }}
                role='button'
                tabIndex={0}
              >
                <div className='w-full h-[280px] flex justify-center bg-[#F9F9FA] overflow-hidden'>
                  <img src={demoSvg} alt='Start from an example' className='w-full h-[280px] object-cover' />
                </div>
                <div className='p-4'>
                  <div className='flex justify-between items-start mb-2'>
                    <Text variant='title3' emphasized>
                      Start from an example
                    </Text>
                    <Badge variant='secondary' radius='full' className='flex-shrink-0'>
                      FOR EVERYONE ELSE
                    </Badge>
                  </div>
                  <Text variant='callout' className='text-text-default'>
                    Choose from a range of interactive demos to explore the capabilities of Flatfile's platform
                    firsthand—we'll deploy the code for you so you can see the magic.
                  </Text>
                </div>
              </div>
            </div>

            <div className='text-center mt-10 mb-6'>
              <Button
                size='lg'
                icon='arrow-right'
                iconPlacement='right'
                onClick={() => {
                  props.onAnyAction?.()
                  choice === 'from scratch' ? nav('/apps/custom/:id/start') : nav('/apps/custom/start')
                  closeModal()
                }}
              >
                Continue {choice}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {props.hidden ? null : props.mini ? (
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              className='inline-flex items-center justify-center w-6 h-6 rounded border border-input bg-transparent'
              onClick={openModal}
              type='button'
            >
              <Icon name='plus-small' size={16} />
            </button>
          </TooltipTrigger>
          <TooltipContent className='flex items-center px-2 py-1'>
            <Text variant='body' className='text-sm'>
              Add an App
            </Text>
          </TooltipContent>
        </Tooltip>
      ) : (
        <Button
          variant='outline'
          size='sm'
          icon={props.iconPlus ? 'plus-small' : undefined}
          iconPlacement='left'
          slotRight={<Icon name='arrow-right' size={16} />}
          onClick={openModal}
        >
          {props.title || 'Get started'}
        </Button>
      )}
    </>
  )
}
